/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get as getUser } from 'js/lib/user';

import { Typography2 } from '@coursera/cds-core';

import { CoachIcon } from 'bundles/ai-coach-platform';
import type {
  QuickMessage as QuickMessageType,
  SendMessageFunc,
} from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { firstNameFromFullName } from 'bundles/ai-coach-platform/utils/nameParser';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';
import QuickMessageV2 from 'bundles/ai-course-coach/components/HomeView/QuickMessageV2';
import { useTracker } from 'bundles/page/lib/event-pulse/react';

import _t from 'i18n!nls/learning-assistant-chat-ai';

type Props = {
  title?: string;
  subtitle?: string;
  quickMessages?: QuickMessageType[];
  sendMessage: SendMessageFunc;
};

const styles = {
  heading: css`
    margin: 0 0 var(--cds-spacing-300) 0;
  `,
  icon: css`
    margin: var(--cds-spacing-300) 0;
    display: flex;
    justify-content: center;
    align-items: center;
  `,

  title: css`
    color: var(--cds-color-emphasis-primary-background-default);
  `,
  subtitle: css`
    margin: 0;
  `,
  quickMessagesContainer: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);

    .coach-secondary-button {
      max-width: 100%;
    }
  `,
};

export const EmptyState = ({ quickMessages, sendMessage }: Props) => {
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const { fullName: userName } = getUser();
  const userFirstName = userName ? firstNameFromFullName(userName) : null;
  const displayTitle = userFirstName ? _t('Hi, #{userFirstName}!', { userFirstName }) : _t('Hi, learner!');
  const displaySubtitle = _t('How can I help?');

  const homeViewQuickMessages = quickMessages || [];

  const coachIconToRender = <CoachIcon size="large" />;

  return (
    <div
      className="coach-home-view"
      css={css`
        padding: 0 var(--cds-spacing-300) var(--cds-spacing-300);
        height: auto;
        background-color: var(--cds-color-neutral-primary-invert);
        border-radius: 0 16px 16px 16px;
      `}
      aria-live="polite"
    >
      <div css={styles.heading}>
        <div css={styles.icon}>{coachIconToRender}</div>
        <Typography2 component="h2" variant="titleSmall" css={styles.title}>
          {displayTitle}
        </Typography2>
        <Typography2 component="h3" variant="titleSmall" css={styles.subtitle}>
          {displaySubtitle}
        </Typography2>
      </div>
      <div css={styles.quickMessagesContainer} role="group" aria-label={_t('Quick start buttons')}>
        {homeViewQuickMessages.map(({ message, action, onClick }) => (
          <QuickMessageV2
            key={message}
            message={message}
            onClick={() => {
              sendMessage(message, action);
              onClick?.();

              if (trackingContext) {
                // v3 tracking
                track('interact_coach', {
                  coachInteractionType: 'click_action',
                  coach: {
                    mode: trackingContext.mode,
                    ...(trackingContext.location && { location: trackingContext.location }),
                  },
                  coachAction: action,
                  ...(trackingContext.courseId && {
                    product: {
                      type: 'course',
                      id: trackingContext.courseId as string,
                    },
                  }),
                  ...(trackingContext.itemId && {
                    courseItem: {
                      id: trackingContext.itemId as string,
                    },
                  }),
                });
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default EmptyState;
